import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Sparkles } from 'lucide-react';

import { useTheme } from 'components/core/theme-provider';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import { useGetAgentSkills } from 'pages/skills/use-skills';
import type { SkillListItem } from 'pages/skills/skills.types';
import { getAskAgentOwnerToAddMessage } from './ask-agent-owner-message';
import { AgentConfigSkillCard } from './agent-config-skill-card';

type SkillsSectionProps = {
  enabled: boolean;
  canEdit?: boolean;
  selectedSkillIds: string[];
  onRemoveSkill: (skillId: string) => void;
};

export function SkillsSection({
  enabled,
  canEdit = true,
  selectedSkillIds,
  onRemoveSkill,
}: SkillsSectionProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { data: skillsData, isLoading } = useGetAgentSkills(
    { page: 1, pageSize: 100 },
    { scope: 'agents-v2-config', enabled: selectedSkillIds.length > 0 }
  );

  const skillsById = useMemo(() => {
    const map = new Map<string, SkillListItem>();
    for (const skill of skillsData?.data ?? []) {
      map.set(skill.id, skill);
    }
    return map;
  }, [skillsData?.data]);

  const selectedSkills = useMemo(
    () =>
      selectedSkillIds.map((id) => {
        const skill = skillsById.get(id);
        if (skill) return skill;

        return {
          id,
          name: id,
          displayName: null,
          description: null,
          iconUrl: null,
        } satisfies Pick<SkillListItem, 'id' | 'name' | 'displayName' | 'description' | 'iconUrl'>;
      }),
    [selectedSkillIds, skillsById]
  );

  const mutedTextColor = theme === 'dark' ? 'text-muted-foreground' : 'text-grey-500';

  if (isLoading && selectedSkillIds.length > 0) {
    return (
      <div className="grid gap-4" data-testid="skills-section-loading">
        {selectedSkillIds.map((id) => (
          <Skeleton key={id} className="h-20 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {selectedSkills.length > 0 ? (
        <div className="grid gap-4">
          {selectedSkills.map((skill) => (
            <AgentConfigSkillCard
              key={skill.id}
              skill={skill}
              onRemove={onRemoveSkill}
              disabled={!enabled}
            />
          ))}
        </div>
      ) : (
        <div
          className={`flex h-36 flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-grey-100 bg-grey-50 px-4 text-center dark:border-border dark:bg-muted/30 ${!enabled ? 'opacity-50' : ''}`}
        >
          <Sparkles className={cn('size-8', mutedTextColor)} strokeWidth={1.5} />
          <p className={cn('w-full max-w-96 text-xs font-medium leading-body', mutedTextColor)}>
            {canEdit
              ? t('NO_SKILLS_ADDED_CLICK_ADD_TO_GET_STARTED') ||
                "No skills added yet. Click 'Add' to get started."
              : getAskAgentOwnerToAddMessage(t, t('SKILLS') || 'skills')}
          </p>
        </div>
      )}
    </div>
  );
}
